import { useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { getAcademicYearLabel, normalizeAcademicYear } from "../utils/academicYear";

const DEFAULT_STRUCTURE = {
  monthlyTuitionFee: 500,
  monthlyMaintenanceFee: 180,
  monthlySportsFee: 150,
  monthlyComputerFee: 150,
  monthlyFeeMonths: 12,
  otherAnnualCharge: 1800,
  otherIdCharge: 0,
  otherDiaryCharge: 0,
  otherExamFee: 0,
  transportFeePerMonth: 500,
  transportMonths: 11,
  customCharges: [],
};

function toNumber(value) {
  const parsed = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

function startsWithRomanNumeral(value) {
  const token = String(value || "")
    .trim()
    .split(/[\s/_-]+/)[0];
  return Boolean(token) && /^[IVX]+$/i.test(token);
}
function isAlphabeticClass(value) {
  const trimmed = String(value || "").trim();
  return /^[A-Za-z]/.test(trimmed) && !startsWithRomanNumeral(trimmed);
}

function normalizeCustomCharges(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (typeof item === "string") {
        const label = item.trim();
        if (!label) return null;
        return { label, amount: "0" };
      }
      const label = String(item?.label ?? item?.name ?? item?.fieldName ?? "").trim();
      if (!label) return null;
      const amountValue = item?.amount ?? item?.value ?? 0;
      return { label, amount: String(toNumber(amountValue)) };
    })
    .filter(Boolean);
}

function safeClassName(value) {
  return String(value || "").trim();
}

function classKey(value) {
  return safeClassName(value)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

function feeStructureId(academicYear, className) {
  const yearKey = normalizeAcademicYear(academicYear);
  return `${yearKey}__${classKey(className) || "unknown"}`;
}

function formatCurrency(value) {
  return `INR ${toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function buildForm(structure, academicYear, className) {
  return {
    academicYear: normalizeAcademicYear(academicYear),
    className: safeClassName(className),
    monthlyTuitionFee: String(
      structure?.monthlyTuitionFee ?? DEFAULT_STRUCTURE.monthlyTuitionFee,
    ),
    monthlyMaintenanceFee: String(
      structure?.monthlyMaintenanceFee ?? DEFAULT_STRUCTURE.monthlyMaintenanceFee,
    ),
    monthlySportsFee: String(
      structure?.monthlySportsFee ?? DEFAULT_STRUCTURE.monthlySportsFee,
    ),
    monthlyComputerFee: String(
      structure?.monthlyComputerFee ?? DEFAULT_STRUCTURE.monthlyComputerFee,
    ),
    monthlyFeeMonths: String(
      structure?.monthlyFeeMonths ?? DEFAULT_STRUCTURE.monthlyFeeMonths,
    ),
    otherAnnualCharge: String(
      structure?.otherAnnualCharge ?? DEFAULT_STRUCTURE.otherAnnualCharge,
    ),
    otherIdCharge: String(structure?.otherIdCharge ?? DEFAULT_STRUCTURE.otherIdCharge),
    otherDiaryCharge: String(
      structure?.otherDiaryCharge ?? DEFAULT_STRUCTURE.otherDiaryCharge,
    ),
    otherExamFee: String(structure?.otherExamFee ?? DEFAULT_STRUCTURE.otherExamFee),
    customCharges: normalizeCustomCharges(structure?.customCharges),
    transportFeePerMonth: String(
      structure?.transportFeePerMonth ?? DEFAULT_STRUCTURE.transportFeePerMonth,
    ),
    transportMonths: String(
      structure?.transportMonths ?? DEFAULT_STRUCTURE.transportMonths,
    ),
  };
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  className = "",
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      />
    </label>
  );
}

export default function FeeStructureManager() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [academicYear, setAcademicYear] = useState(getAcademicYearLabel());
  const [selectedClass, setSelectedClass] = useState("");
  const [formData, setFormData] = useState(buildForm(null, academicYear, ""));
  const [feeStructures, setFeeStructures] = useState([]);
  const [classOptions, setClassOptions] = useState([]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          const isAdminUser =
            (data.role && String(data.role).toLowerCase() === "admin") ||
            (Array.isArray(data.roles) && data.roles.includes("admin"));
          setIsAdmin(isAdminUser);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("Failed to check admin role:", err);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStructures();
    fetchClasses();
  }, [isAdmin]);

  useEffect(() => {
    setFormData(buildForm(null, academicYear, selectedClass));
  }, [academicYear, selectedClass]);

  async function fetchStructures() {
    setError("");
    try {
      const snap = await getDocs(collection(db, "fee_structures"));
      const list = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setFeeStructures(list);
    } catch (err) {
      console.error("Failed to fetch fee structures:", err);
      setError("Unable to load fee structures.");
    }
  }

  async function fetchClasses() {
    try {
      const snap = await getDocs(collection(db, "students"));
      const classes = Array.from(
        new Set(
          snap.docs
            .map((docSnap) => safeClassName(docSnap.data().className || docSnap.data().class))
            .filter(Boolean),
        ),
      )
        .filter(isAlphabeticClass)
        .sort();
      setClassOptions(classes);
    } catch (err) {
      console.error("Failed to fetch class list:", err);
      setClassOptions([]);
    }
  }

  const availableYears = useMemo(() => {
    const years = new Set(
      feeStructures.map((structure) =>
        normalizeAcademicYear(structure.academicYear),
      ),
    );
    years.add(normalizeAcademicYear(academicYear));
    return Array.from(years).filter(Boolean).sort().reverse();
  }, [feeStructures, academicYear]);

  const currentStructure = useMemo(() => {
    if (!selectedClass) return null;
    const match = feeStructures.find(
      (structure) =>
        normalizeAcademicYear(structure.academicYear) ===
          normalizeAcademicYear(academicYear) &&
        safeClassName(structure.className).toLowerCase() ===
          safeClassName(selectedClass).toLowerCase(),
    );
    return match || null;
  }, [feeStructures, academicYear, selectedClass]);

  function updateField(field, value) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }
  function updateCustomCharge(index, field, value) {
    setFormData((prev) => {
      const next = Array.isArray(prev.customCharges) ? [...prev.customCharges] : [];
      next[index] = { ...(next[index] || {}), [field]: value };
      return { ...prev, customCharges: next };
    });
  }
  function addCustomCharge() {
    setFormData((prev) => ({
      ...prev,
      customCharges: [...(Array.isArray(prev.customCharges) ? prev.customCharges : []), { label: "", amount: "" }],
    }));
  }
  function removeCustomCharge(index) {
    setFormData((prev) => ({
      ...prev,
      customCharges: (Array.isArray(prev.customCharges) ? prev.customCharges : []).filter(
        (_, i) => i !== index,
      ),
    }));
  }

  async function handleSave() {
    setMessage("");
    setError("");

    const year = normalizeAcademicYear(academicYear);
    const className = safeClassName(selectedClass);

    if (!year || !/^\d{4}-\d{4}$/.test(year)) {
      setError("Academic year must be in YYYY-YYYY format.");
      return;
    }
    if (!className) {
      setError("Class name is required.");
      return;
    }

    const customCharges = normalizeCustomCharges(formData.customCharges).map(
      (item) => ({
        label: item.label,
        amount: toNumber(item.amount),
      }),
    );

    const payload = {
      academicYear: year,
      className,
      monthlyTuitionFee: toNumber(formData.monthlyTuitionFee),
      monthlyMaintenanceFee: toNumber(formData.monthlyMaintenanceFee),
      monthlySportsFee: toNumber(formData.monthlySportsFee),
      monthlyComputerFee: toNumber(formData.monthlyComputerFee),
      monthlyFeeMonths: Math.max(1, Math.min(12, toNumber(formData.monthlyFeeMonths))),
      otherAnnualCharge: toNumber(formData.otherAnnualCharge),
      otherIdCharge: toNumber(formData.otherIdCharge),
      otherDiaryCharge: toNumber(formData.otherDiaryCharge),
      otherExamFee: toNumber(formData.otherExamFee),
      customCharges,
      transportFeePerMonth: toNumber(formData.transportFeePerMonth),
      transportMonths: Math.max(1, Math.min(12, toNumber(formData.transportMonths))),
      updatedAt: serverTimestamp(),
    };

    try {
      setSaving(true);
      const docId = feeStructureId(year, className);
      await setDoc(doc(db, "fee_structures", docId), payload, { merge: true });
      await fetchStructures();
      setMessage("Fee structure saved.");
    } catch (err) {
      console.error("Failed to save fee structure:", err);
      setError("Unable to save fee structure.");
    } finally {
      setSaving(false);
    }
  }

  function handleLoadExisting() {
    if (!currentStructure) return;
    setFormData(buildForm(currentStructure, academicYear, selectedClass));
    setMessage("Loaded existing structure for editing.");
  }

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <p className="text-gray-500">Checking access...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <p className="text-gray-500">Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm mt-6 p-4 sm:p-6 border border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            Fee Structure (Per Class, Per Year)
          </h3>
          <p className="text-sm text-gray-500">
            Update every academic session before fee collection starts.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <label className="block">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Academic Year
          </span>
          <input
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            list="academic-year-list"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="YYYY-YYYY"
          />
          <datalist id="academic-year-list">
            {availableYears.map((year) => (
              <option key={year} value={year} />
            ))}
          </datalist>
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Class
          </span>
          <input
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            list="class-list"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="e.g. Class 5"
          />
          <datalist id="class-list">
            {classOptions.map((cls) => (
              <option key={cls} value={cls} />
            ))}
          </datalist>
        </label>

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={handleLoadExisting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            disabled={!currentStructure}
          >
            Load Existing
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Structure"}
          </button>
        </div>
      </div>

      {(message || error) && (
        <div
          className={`mb-4 rounded-lg px-3 py-2 text-sm ${
            error
              ? "border border-red-200 bg-red-50 text-red-700"
              : "border border-green-200 bg-green-50 text-green-700"
          }`}
        >
          {error || message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
        <InputField
          label="Tuition / Month"
          type="number"
          value={formData.monthlyTuitionFee}
          onChange={(e) => updateField("monthlyTuitionFee", e.target.value)}
        />
        <InputField
          label="Maintenance / Month"
          type="number"
          value={formData.monthlyMaintenanceFee}
          onChange={(e) => updateField("monthlyMaintenanceFee", e.target.value)}
        />
        <InputField
          label="Sports / Month"
          type="number"
          value={formData.monthlySportsFee}
          onChange={(e) => updateField("monthlySportsFee", e.target.value)}
        />
        <InputField
          label="Computer / Month"
          type="number"
          value={formData.monthlyComputerFee}
          onChange={(e) => updateField("monthlyComputerFee", e.target.value)}
        />
        <InputField
          label="Monthly Months"
          type="number"
          value={formData.monthlyFeeMonths}
          onChange={(e) => updateField("monthlyFeeMonths", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
        <InputField
          label="Annual Charge"
          type="number"
          value={formData.otherAnnualCharge}
          onChange={(e) => updateField("otherAnnualCharge", e.target.value)}
        />
        <InputField
          label="ID Charge"
          type="number"
          value={formData.otherIdCharge}
          onChange={(e) => updateField("otherIdCharge", e.target.value)}
        />
        <InputField
          label="Diary Charge"
          type="number"
          value={formData.otherDiaryCharge}
          onChange={(e) => updateField("otherDiaryCharge", e.target.value)}
        />
        <InputField
          label="Exam Fee"
          type="number"
          value={formData.otherExamFee}
          onChange={(e) => updateField("otherExamFee", e.target.value)}
        />
        <div />
      </div>

      <div className="rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Custom Charges</h4>
            <p className="text-xs text-gray-500">
              Add extra charge fields that apply to all students in this class/year.
            </p>
          </div>
          <button
            type="button"
            onClick={addCustomCharge}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold hover:bg-gray-50"
          >
            Add Field
          </button>
        </div>
        {(!formData.customCharges || formData.customCharges.length === 0) && (
          <p className="text-xs text-gray-500">No custom charges added yet.</p>
        )}
        {formData.customCharges && formData.customCharges.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formData.customCharges.map((charge, index) => (
              <div key={`${charge.label}-${index}`} className="flex items-end gap-2">
                <InputField
                  label="Field name"
                  value={charge.label}
                  onChange={(e) => updateCustomCharge(index, "label", e.target.value)}
                  className="flex-1"
                />
                <InputField
                  label="Amount"
                  type="number"
                  value={charge.amount}
                  onChange={(e) => updateCustomCharge(index, "amount", e.target.value)}
                  className="w-40"
                />
                <button
                  type="button"
                  onClick={() => removeCustomCharge(index)}
                  className="mb-1 px-2.5 py-2 rounded-lg border border-gray-300 text-xs font-semibold hover:bg-gray-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
        <InputField
          label="Transport / Month"
          type="number"
          value={formData.transportFeePerMonth}
          onChange={(e) => updateField("transportFeePerMonth", e.target.value)}
        />
        <InputField
          label="Transport Months"
          type="number"
          value={formData.transportMonths}
          onChange={(e) => updateField("transportMonths", e.target.value)}
        />
        <div />
        <div />
        <div />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Academic Year</th>
              <th className="px-3 py-2 text-left">Class</th>
              <th className="px-3 py-2 text-left">Monthly Total</th>
              <th className="px-3 py-2 text-left">Other Charges</th>
              <th className="px-3 py-2 text-left">Transport</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {feeStructures.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                  No fee structures added yet.
                </td>
              </tr>
            )}
            {feeStructures.map((structure) => {
              const monthlyTotal =
                toNumber(structure.monthlyTuitionFee) +
                toNumber(structure.monthlyMaintenanceFee) +
                toNumber(structure.monthlySportsFee) +
                toNumber(structure.monthlyComputerFee);
              const customTotal = Array.isArray(structure.customCharges)
                ? structure.customCharges.reduce(
                    (sum, item) => sum + toNumber(item?.amount),
                    0,
                  )
                : 0;
              const otherTotal =
                toNumber(structure.otherAnnualCharge) +
                toNumber(structure.otherIdCharge) +
                toNumber(structure.otherDiaryCharge) +
                toNumber(structure.otherExamFee) +
                customTotal;
              const transportLabel = `${formatCurrency(
                structure.transportFeePerMonth,
              )} x ${structure.transportMonths || DEFAULT_STRUCTURE.transportMonths}`;

              return (
                <tr
                  key={structure.id}
                  className="hover:bg-indigo-50/40 cursor-pointer"
                  onClick={() => {
                    setAcademicYear(normalizeAcademicYear(structure.academicYear));
                    setSelectedClass(structure.className);
                    setFormData(buildForm(structure, structure.academicYear, structure.className));
                  }}
                >
                  <td className="px-3 py-2">{structure.academicYear}</td>
                  <td className="px-3 py-2">{structure.className}</td>
                  <td className="px-3 py-2">{formatCurrency(monthlyTotal)}</td>
                  <td className="px-3 py-2">{formatCurrency(otherTotal)}</td>
                  <td className="px-3 py-2">{transportLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
